import { useEffect } from "react";
import { api } from "@/api/client";
import { invokeEngine } from "@/engine/hooks";
import { hostMode } from "@/engine/transport";
import { useSession } from "./session";

const BATCH = 10;
const BUSY_MS = 5_000;
const IDLE_MS = 5 * 60_000;

/**
 * In a browser, the people's cities and postal codes are looked up through the server so the map can place them.
 * The Mac app does this with Apple's geocoder and the iPhone with MapKit; only the coarse places go out from here,
 * never a street address. Runs a small batch every few seconds while there is work, then checks back now and then.
 */
export function useWebPlaceLookups(): void {
  const signedIn = useSession((s) => s.isSignedIn);
  useEffect(() => {
    if (hostMode() !== "web" || !signedIn) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const step = async () => {
      let next = IDLE_MS;
      try {
        const candidates = await invokeEngine("listGeocodeCandidates", { limit: BATCH, coarse: true });
        if (candidates.length) {
          const { results } = await api.places.lookup(candidates.map((c) => c.query));
          if (results.length) await invokeEngine("applyGeocodes", { results });
          next = BUSY_MS;
        }
      } catch {
        // Offline, an older server without the route, or the engine still opening: try again later.
      }
      if (!stopped) timer = setTimeout(() => void step(), next);
    };
    timer = setTimeout(() => void step(), BUSY_MS);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [signedIn]);
}
